import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Chip,
  Button,
  Tooltip,
  IconButton,
  alpha,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import axiosInstance from '../../../utils/axios';
import { canManageAnalyses } from '../../../utils/analysisPermissions';
import {
  getAnalysisDocumentFilename,
  getAnalysisDocumentKind,
  getAnalysisDocumentTypeLabel,
} from '../../../utils/analysisDocument';
import {
  fetchAnalysisPreviewBlob,
  downloadAnalysisDocument,
  openAnalysisDocumentInNewTab,
  openBlobUrlInNewTab,
} from '../../../utils/protectedFilePreview';
import { toStorageUrl } from '../../../utils/storageUrl';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Analytics as AnalyticsIcon,
  Person as PersonIcon,
  PictureAsPdf as PdfIcon,
  GridOn as ExcelIcon,
  ModelTraining as TrainingIcon,
  Home as DomesticIcon,
  Public as InternationalIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  School as UniversityIcon,
  LocationOn as LocationIcon,
  Assessment as AssessmentIcon,
  AccessTime as TimeIcon,
  Description as DescriptionIcon,
  Visibility as ViewIcon,
  OpenInNew as OpenInNewIcon,
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { SUCCESS } from '../../common/SaaSTokens';
import {
  injectDetailsPageStyles,
} from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';

// ─── Design tokens – cyberpunk dark system ────────────────────────────────────
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';
const SURFACE_TOP  = '#152640';

const TEXT_BODY  = '#E2E8F0';
const TEXT_MUTED = '#94A3B8';

// ─── ActionButtons ────────────────────────────────────────────────────────────
const ActionButtons = ({ canEditAnalysis, canDeleteAnalysis, onEdit, onDelete }) => {
  if (!canEditAnalysis && !canDeleteAnalysis) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {canEditAnalysis && (
        <Button
          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
          onClick={() => onEdit()}
          sx={{
            borderRadius: '10px', px: 2.5, py: 0.7,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
            background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.6)}`,
            color: NEON,
            transition: 'all 0.2s ease',
            '&:hover': { 
              background: alpha(NEON, 0.08), 
              borderColor: NEON,
              boxShadow: `0 0 12px ${alpha(NEON, 0.2)}`
            },
          }}
        >
          Edit
        </Button>
      )}
      {canDeleteAnalysis && (
        <Button
          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
          onClick={() => onDelete()}
          sx={{
            borderRadius: '10px', px: 2.5, py: 0.7,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
            background: 'transparent', border: `1.5px solid ${alpha('#F87171', 0.6)}`,
            color: '#F87171',
            transition: 'all 0.2s ease',
            '&:hover': { 
              background: alpha('#F87171', 0.08), 
              borderColor: '#F87171',
              boxShadow: `0 0 12px ${alpha('#F87171', 0.2)}`
            },
          }}
        >
          Delete
        </Button>
      )}
    </Box>
  );
};

const MetricCard = ({ label, value, icon: Icon, color = NEON, sub }) => (
  <Box sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const AnalysisDocumentPreviewSection = ({ analysis, documentPreviewUrl, documentKind, onDownload, onOpenInTab }) => {
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const iframeRef = useRef(null);
  const viewerHeight = { xs: 460, md: 640 };
  const isPdfPreview = documentKind === 'pdf';
  const typeLabel = getAnalysisDocumentTypeLabel(documentKind);
  const DocIcon = documentKind === 'excel' ? ExcelIcon : PdfIcon;

  const handleOpenInTab = () => {
    onOpenInTab?.();
  };

  const handleRefresh = () => {
    setIframeError(false);
    setIframeLoaded(false);
    if (iframeRef.current) {
      const src = iframeRef.current.src;
      iframeRef.current.src = '';
      setTimeout(() => { iframeRef.current.src = src; }, 50);
    }
  };

  return (
    <Section title="Analysis Document Preview" icon={ViewIcon} delay={0.12}>
      {documentPreviewUrl ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.25,
            borderRadius: '10px 10px 0 0',
            background: alpha(SURFACE_UP, 0.9),
            border: `1px solid ${alpha(NEON, 0.14)}`,
            borderBottom: 'none',
            backdropFilter: 'blur(8px)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: '7px',
                background: `linear-gradient(135deg, ${alpha(NEON, 0.25)}, ${alpha(NEON2, 0.08)})`,
                border: `1px solid ${alpha(NEON, 0.35)}`,
              }}>
                <DocIcon sx={{ fontSize: 16, color: documentKind === 'excel' ? NEON3 : NEON }} />
              </Box>
              <Box>
                <Typography sx={{
                  fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem',
                  fontWeight: 600, color: TEXT_BODY,
                  maxWidth: { xs: 140, sm: 280, md: 420 },
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {analysis.name}
                </Typography>
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.7), letterSpacing: 0.5 }}>
                  {typeLabel} Analysis Document
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 1 }}>
              <Tooltip title="Refresh" placement="top">
                <IconButton onClick={handleRefresh} size="small" sx={{ color: TEXT_MUTED, borderRadius: '7px', '&:hover': { background: alpha(NEON, 0.08), color: NEON } }}>
                  <RefreshIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab" placement="top">
                <IconButton onClick={handleOpenInTab} size="small" sx={{ color: TEXT_MUTED, borderRadius: '7px', '&:hover': { background: alpha(NEON, 0.08), color: NEON } }}>
                  <OpenInNewIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Button
                size="small"
                startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                onClick={onDownload}
                sx={{
                  px: 1.5, py: 0.4, ml: 0.5, borderRadius: '7px',
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.78rem',
                  whiteSpace: 'nowrap', flexShrink: 0,
                  background: alpha(NEON, 0.1), border: `1px solid ${alpha(NEON, 0.35)}`,
                  color: NEON, transition: 'all 0.2s ease',
                  '&:hover': { background: alpha(NEON, 0.18), borderColor: NEON },
                }}
              >
                Download
              </Button>
            </Box>
          </Box>

          <Box sx={{
            position: 'relative',
            height: viewerHeight,
            borderRadius: '0 0 10px 10px',
            overflow: 'hidden',
            border: `1px solid ${alpha(NEON, 0.14)}`,
            background: '#0a0e1a',
          }}>
            {isPdfPreview && !iframeLoaded && !iframeError && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 2,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 2,
                background: '#0a0e1a',
              }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: '12px',
                  background: `linear-gradient(135deg, ${alpha(NEON, 0.2)}, ${alpha(NEON2, 0.05)})`,
                  border: `1px solid ${alpha(NEON, 0.25)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <PdfIcon sx={{ fontSize: 22, color: NEON }} />
                </Box>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: alpha(TEXT_MUTED, 0.7) }}>
                  Loading document...
                </Typography>
              </Box>
            )}

            {isPdfPreview && iframeError && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 2,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 2.5,
                background: '#0a0e1a',
              }}>
                <Box sx={{
                  width: 56, height: 56, borderRadius: '14px',
                  background: alpha('#F87171', 0.1), border: `1px solid ${alpha('#F87171', 0.25)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ErrorIcon sx={{ fontSize: 26, color: '#F87171' }} />
                </Box>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: TEXT_BODY }}>
                  Preview unavailable
                </Typography>
              </Box>
            )}

            {isPdfPreview ? (
              <iframe
                ref={iframeRef}
                src={`${documentPreviewUrl}#toolbar=0&navpanes=0&view=FitH`}
                style={{ width: '100%', height: '100%', border: 'none', opacity: iframeLoaded && !iframeError ? 1 : 0, transition: 'opacity 0.3s ease' }}
                title={analysis.name}
                onLoad={() => setIframeLoaded(true)}
                onError={() => setIframeError(true)}
              />
            ) : (
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 2, height: '100%', px: 3, textAlign: 'center',
              }}>
                <Box sx={{
                  width: 56, height: 56, borderRadius: '14px',
                  background: `linear-gradient(135deg, ${alpha(NEON3, 0.2)}, ${alpha(NEON3, 0.05)})`,
                  border: `1px solid ${alpha(NEON3, 0.3)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ExcelIcon sx={{ fontSize: 28, color: NEON3 }} />
                </Box>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.95rem', color: TEXT_BODY }}>
                  Excel preview not available inline
                </Typography>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: alpha(TEXT_MUTED, 0.75), maxWidth: 420 }}>
                  Download the file or open it in a new tab to view it in Excel or another spreadsheet application.
                </Typography>
                <Button
                  size="small"
                  startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  href={documentPreviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    px: 1.75, py: 0.5, borderRadius: '8px',
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.8rem',
                    background: alpha(NEON3, 0.1), border: `1px solid ${alpha(NEON3, 0.35)}`,
                    color: NEON3, '&:hover': { background: alpha(NEON3, 0.18), borderColor: NEON3 },
                  }}
                >
                  Open in new tab
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 0.85, mt: 0,
            borderRadius: '0 0 8px 8px',
            background: alpha(SURFACE_MID, 0.6),
            border: `1px solid ${alpha(NEON, 0.08)}`,
            borderTop: `1px solid ${alpha(NEON, 0.06)}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                background: isPdfPreview
                  ? (iframeLoaded && !iframeError ? NEON3 : iframeError ? '#F87171' : NEON2)
                  : NEON3,
                boxShadow: `0 0 5px ${isPdfPreview
                  ? (iframeLoaded && !iframeError ? NEON3 : iframeError ? '#F87171' : NEON2)
                  : NEON3}`,
              }} />
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.6), letterSpacing: 0.4 }}>
                {isPdfPreview
                  ? (iframeLoaded && !iframeError ? 'Ready' : iframeError ? 'Load error' : 'Loading...')
                  : 'Download to view'}
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(TEXT_MUTED, 0.45), letterSpacing: 0.4 }}>
              {isPdfPreview ? 'PDF · Inline viewer' : `${typeLabel} · Download to view`}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 2.5, py: 6, px: 3, textAlign: 'center',
          borderRadius: '10px',
          border: `1px dashed ${alpha(NEON, 0.14)}`,
          background: alpha(SURFACE_UP, 0.3),
        }}>
          <Box sx={{
            width: 60, height: 60, borderRadius: '14px',
            background: alpha(SURFACE_UP, 0.8), border: `1px solid ${alpha(NEON, 0.14)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PdfIcon sx={{ fontSize: 26, color: alpha(TEXT_MUTED, 0.5) }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.92rem', color: TEXT_BODY, mb: 0.5 }}>
              No preview available
            </Typography>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: TEXT_MUTED }}>
              This analysis does not have a document attached for preview.
            </Typography>
          </Box>
          <Button
            startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
            onClick={onDownload}
            sx={{
              px: 2.5, py: 0.7, borderRadius: '9px',
              fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.84rem',
              background: alpha(NEON, 0.1), border: `1.5px solid ${alpha(NEON, 0.4)}`,
              color: NEON, transition: 'all 0.2s ease',
              '&:hover': { background: alpha(NEON, 0.18), borderColor: NEON },
            }}
          >
            Download Instead
          </Button>
        </Box>
      )}
    </Section>
  );
};

const AnalysisDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);

  const canManage = canManageAnalyses(currentUser);
  const canEditAnalysis = canManage;
  const canDeleteAnalysis = canManage;

  const formatAuditUser = (u) => (u?.name ? String(u.name) : null);

  useEffect(() => {
    injectDetailsPageStyles();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchAnalysisDetails();
  }, [id, currentUser, navigate]);

  const fetchAnalysisDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/analyses/${id}`);
      setAnalysis(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching analysis details:', error);
      if (error.response?.status === 403) {
        setError('You do not have access to this analysis. Private analyses are limited to Team Leaders and Game Management members.');
      } else {
        setError('Failed to fetch analysis details. Please try again.');
      }
      if (error.response?.status === 401) {
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnalysis = async () => {
    try {
      const analysisName = analysis?.name || 'Analysis';
      await axiosInstance.delete(`/analyses/${id}`);
      setDeleteDialogOpen(false);
      setSuccessMessage(`Analysis "${analysisName}" has been deleted successfully!`);
      setTimeout(() => navigate('/analyses'), 2000);
    } catch (error) {
      console.error('Error deleting analysis:', error);
      setError('Failed to delete analysis. Please try again.');
      setDeleteDialogOpen(false);
    }
  };

  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;

    const loadPreview = async () => {
      if (!analysis?.document_pdf) {
        setDocumentPreviewUrl(null);
        return;
      }

      const publicUrl = toStorageUrl(analysis.document_pdf);
      const kind = getAnalysisDocumentKind(analysis.document_pdf);
      if (kind !== 'pdf') {
        setDocumentPreviewUrl(publicUrl || null);
        return;
      }

      setDocumentPreviewUrl(publicUrl || null);

      try {
        const preview = await fetchAnalysisPreviewBlob(analysis.id);
        if (cancelled) {
          URL.revokeObjectURL(preview.url);
          return;
        }
        revokeUrl = preview.url;
        setDocumentPreviewUrl(preview.url);
      } catch {
        if (!cancelled && publicUrl) setDocumentPreviewUrl(publicUrl);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [analysis?.id, analysis?.document_pdf]);

  const handleDownload = async () => {
    if (!analysis?.id) return;
    try {
      await downloadAnalysisDocument(
        analysis.id,
        getAnalysisDocumentFilename(analysis.name, analysis.document_pdf),
      );
      setSuccessMessage('Document downloaded successfully.');
    } catch (e) {
      console.error(e);
      setError('Failed to download document.');
    }
  };

  const handleOpenInTab = async () => {
    if (!analysis?.id) return;
    try {
      if (documentPreviewUrl?.startsWith('blob:')) {
        openBlobUrlInNewTab(documentPreviewUrl);
      } else {
        await openAnalysisDocumentInNewTab(analysis.id);
      }
      setError(null);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to open document.');
    }
  };

  const getTypeDescription = (type) => {
    switch (type) {
      case 'training': return 'Training and development programs';
      case 'domestic': return 'Local universities competitor analysis';
      case 'international': return 'Global universities competitor analysis';
      default: return 'Unknown analysis type';
    }
  };

  const publicityCfg = analysis?.publicity === 'private'
    ? { label: 'Private', color: '#F87171' }
    : { label: 'Public', color: NEON3 };

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="Please log in to view this page."
          severity="error"
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.9rem',
            fontWeight: 500,
            background: alpha('#450a0a', 0.92),
            border: '1px solid rgba(248,113,113,0.3)',
            boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
            color: '#fca5a5',
          }}
        />
      </Container>
    );
  }

  if (loading) {
    return <Loading message="Loading analysis details" size={56} variant="primary" />;
  }

  if (error && !analysis) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message={error}
          severity="error"
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.9rem',
            fontWeight: 500,
            background: alpha('#450a0a', 0.92),
            border: '1px solid rgba(248,113,113,0.3)',
            boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
            color: '#fca5a5',
          }}
        />
      </Container>
    );
  }

  if (!analysis) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage
          message="Analysis not found"
          severity="error"
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '0.9rem',
            fontWeight: 500,
            background: alpha('#450a0a', 0.92),
            border: '1px solid rgba(248,113,113,0.3)',
            boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
            color: '#fca5a5',
          }}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      
      {/* Header with ActionButtons */}
      <Header
        title="Analysis Details"
        subtitle="View analysis information"
        icon={AnalyticsIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        actionButton={
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <ActionButtons
              canEditAnalysis={canEditAnalysis}
              canDeleteAnalysis={canDeleteAnalysis}
              onEdit={() => navigate(`/analyses/edit/${id}`)}
              onDelete={() => setDeleteDialogOpen(true)}
            />
          </Box>
        }
      />

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.9rem',
          fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage(null)}
        sx={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.9rem',
          fontWeight: 500,
          background: alpha(SUCCESS, 0.12),
          border: `1px solid ${alpha(SUCCESS, 0.25)}`,
          color: alpha(SUCCESS, 0.95),
          mb: 2,
        }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                border: `2px solid ${alpha(NEON2, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AnalyticsIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                  {analysis.name}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                  {getTypeDescription(analysis.type)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.5, borderRadius: '20px',
              background: alpha(publicityCfg.color, 0.1),
              border: `1px solid ${alpha(publicityCfg.color, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: publicityCfg.color }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: publicityCfg.color, letterSpacing: '0.05em' }}>
                {publicityCfg.label}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <MetricCard label="Type" value={analysis.type?.charAt(0).toUpperCase() + analysis.type?.slice(1)} icon={AssessmentIcon} color={NEON} />
            <MetricCard label="University" value={analysis.university} icon={UniversityIcon} color={NEON2} />
            <MetricCard label="Country" value={analysis.country} icon={LocationIcon} color={NEON3} />
            <MetricCard label="Created" value={analysis.created_at ? format(new Date(analysis.created_at), 'dd MMM yyyy') : null} icon={CalendarIcon} color={NEON} />
          </Box>
        </Box>

        {/* Analysis Information */}
        <Section title="Analysis Remarks & Notes" icon={AssessmentIcon} delay={0.1}>
          <Box sx={{
            px: 2, py: 1.8, borderRadius: '10px',
            borderLeft: `3px solid ${alpha(NEON, 0.4)}`,
            background: alpha(SURFACE_TOP, 0.4),
          }}>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.92rem', color: TEXT_BODY, lineHeight: 1.75 }}>
              {analysis.remark || 'No remarks provided.'}
            </Typography>
          </Box>
        </Section>

        <AnalysisDocumentPreviewSection
          analysis={analysis}
          documentPreviewUrl={documentPreviewUrl}
          documentKind={getAnalysisDocumentKind(analysis?.document_pdf)}
          onDownload={handleDownload}
          onOpenInTab={handleOpenInTab}
        />

        <SystemInformationSection
          createdBy={formatAuditUser(analysis.record_creator) || formatAuditUser(analysis.user)}
          createdAt={format(new Date(analysis.created_at), 'dd MMM yyyy, h:mm a')}
          updatedBy={formatAuditUser(analysis.last_updater)}
          updatedAt={format(new Date(analysis.updated_at), 'dd MMM yyyy, h:mm a')}
          delay={0.18}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteAnalysis}
        itemType="analysis"
        itemName={analysis?.name}
        description="This action cannot be undone and will permanently remove all analysis data including documents and records."
        confirmLabel="Delete Analysis"
      />
    </Container>
  );
};

export default AnalysisDetails;